import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LineChart } from "lucide-react";
import axios, { API } from "./commoditiesLib";

const prettyLabel = (k) =>
  k.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const ConfigValue = ({ v }) => {
  if (v == null) return <span className="text-muted-3">—</span>;
  if (Array.isArray(v)) return <span>[{v.join(", ")}]</span>;
  if (typeof v === "object") {
    return (
      <span className="text-muted-2">
        {Object.entries(v).map(([k2, v2]) => `${k2}: ${v2}`).join(", ")}
      </span>
    );
  }
  return <span>{String(v)}</span>;
};

const StrategyCard = ({ id, meta }) => (
  <div className="border border-grid p-5 space-y-3" data-testid={`commodities-strategy-${id}`}>
    <div className="flex items-center justify-between">
      <h3 className="font-heading text-lg">{meta.label}</h3>
      <span
        className="px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border"
        style={{
          color: meta.enabled ? "var(--bull)" : "var(--text-muted-2)",
          borderColor: meta.enabled ? "var(--bull)" : "var(--grid)",
        }}
      >
        {meta.enabled ? "Enabled" : "Disabled"}
      </span>
    </div>
    <p className="text-xs text-muted-2 leading-relaxed">{meta.description}</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px] font-mono-data pt-2 border-t border-grid/40">
      {Object.entries(meta.config || {}).map(([k, v]) => (
        <div key={k} className="flex justify-between gap-3">
          <span className="text-muted-3">{prettyLabel(k)}</span>
          <span className="text-muted-1 text-right"><ConfigValue v={v} /></span>
        </div>
      ))}
    </div>
  </div>
);

const CommoditiesStrategies = () => {
  const [strategies, setStrategies] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commodities/strategies`);
      setStrategies(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load strategies");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="space-y-4" data-testid="commodities-strategies-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          Commodities · MCX
        </div>
        <h2 className="font-heading text-2xl flex items-center gap-2">
          <LineChart size={20} /> Strategies
        </h2>
      </div>

      {!strategies ? (
        <div className="text-muted-2 font-mono-data text-xs">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(strategies).map(([id, meta]) => (
            <StrategyCard key={id} id={id} meta={meta} />
          ))}
        </div>
      )}
    </section>
  );
};

export default CommoditiesStrategies;
